/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { DeliveryOption } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import get from 'lodash/get';

import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionsViewProps,
    ICheckoutDeliveryOptionsList
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutDeliveryOptionsProps } from '../definition-extensions/checkout-delivery-options.ext.props.autogenerated';
import * as Msdyn365 from '@msdyn365-commerce/core';

interface ICheckoutDeliveryData extends ICheckoutDeliveryOptionsList {
    deliveryOptions: DeliveryOption[];
}

const DeliveryOptionList: React.FC<ICheckoutDeliveryData> = ({ DeliveryOptionsList, list, deliveryOptions }) => {
    if (!list || list.length === 0) {
        return null;
    }

    // deliveryOptions sorted by ShippingChargeAmount
    const sortedDeliveryOptions = deliveryOptions.sort((a, b) => {
        return get(a, 'ShippingChargeAmount', 0) - get(b, 'ShippingChargeAmount', 0);
    });

    // console.log(sortedDeliveryOptions);
    return (
        <Node {...DeliveryOptionsList}>
            {sortedDeliveryOptions.map((deliveryOption: DeliveryOption) => {
                const sortedDeliveryOption = list.find(deliveryOptionEdit => deliveryOptionEdit.code === deliveryOption.Code);
                if (sortedDeliveryOption !== undefined) {
                    return (
                        <Node
                            {...sortedDeliveryOption.DeliveryOption}
                            key={sortedDeliveryOption.code}
                            aria-label='Please select a delivery option.'
                        >
                            {sortedDeliveryOption.radioButton}
                            {sortedDeliveryOption.description}
                            {sortedDeliveryOption.price}
                        </Node>
                    );
                } else {
                    return null;
                }
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({ DeliveryOption, description, price }) => {
    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps & ICheckoutDeliveryOptionsProps<{}>> = props => {
    const {
        CheckoutDeliveryOptions,
        checkoutErrorRef,
        viewState,
        deliveryOptions,
        deliveryOptionsData,
        deliveryOptionSelected,
        alert,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        checkoutCartLineGroupDeliveryOptions,
        enableCheckoutCartLineDeliveryOptions
    } = props;
    const extraTextField = props.config.paragraph;

    const extraText = extraTextField && <Msdyn365.RichTextComponent text={extraTextField} className='ms-checkout__extratext' />;

    const filterElements = React.useCallback((elements: React.ReactNode): React.ReactNode => {
        return React.Children.toArray(elements)
            .filter(
                child =>
                    React.isValidElement(child) &&
                    !child.props.className?.includes('ms-checkout-delivery-options__cart-line-grp__item__ctn-lines')
            )
            .map(child => {
                if (React.isValidElement(child) && child.props.children) {
                    return React.cloneElement(child as React.ReactElement<any>, {
                        children: filterElements(child.props.children)
                    });
                }
                return child;
            });
    }, []);

    const filteredCheckoutCartLineGroupDeliveryOptions = props.config.enableMultipleDeliveryOptions
        ? checkoutCartLineGroupDeliveryOptions
        : filterElements(checkoutCartLineGroupDeliveryOptions);

    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions} ref={checkoutErrorRef}>
            {extraText}
            {viewState.isLoading && waiting}
            {viewState.isError && alert}
            {viewState.isShowList && deliveryOptions && <DeliveryOptionList deliveryOptions={deliveryOptionsData} {...deliveryOptions} />}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowList && enableCheckoutCartLineDeliveryOptions && filteredCheckoutCartLineGroupDeliveryOptions}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
